import BackButton from '@acorn-portal/shared/src/components/buttons/BackButton';
import ContinueButton from '@acorn-portal/shared/src/components/buttons/ContinueButton';
import HelperModal from '@acorn-portal/shared/src/components/modal/HelperModal';
import VehicleLookup from '@acorn-portal/shared/src/components/vehicleLookup';
import ActionButtons from '@acorn-portal/shared/src/components/wrapper/action-buttons';
import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import JourneyCardLayout from '@acorn-portal/shared/src/layouts/journey-card-layout';
import { isEmptyObject } from '@acorn-portal/shared/src/utils';
import { VEHICLE_DETAILS_PART_ONE_ERROR_MESSAGES } from '@acorn-portal/shared/src/utils/constants/ErrorMessages';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { Typography } from '@mui/material';
import { Form, Formik, FormikHelpers } from 'formik';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { amendVehiclePart1Schema } from '../validation/amend-vehicle-part1-schema';
import { FormikLogErrors } from '@acorn-portal/shared/src/pages/mta/components/FormikLogErrors';

interface IChangeOfVehicleSubscriptionPart1 {
	vrm: string;
	make?: string;
	model?: string;
	bodyType?: string;
	year?: number;
	fuelType?: string;
	transmission?: string;
}

const initialValues: IChangeOfVehicleSubscriptionPart1 = {
	vrm: '',
	make: '',
	model: '',
	bodyType: '',
	year: 0,
	fuelType: '',
	transmission: '',
};

const AmendVehiclePart1 = () => {
	const navigate = useNavigate();
	const [open, toggleModal] = useState(false);
	const vehicle = useAppSelector((state) => state.vehicleLookup);
	const previousVehicle = useAppSelector((state) => state.subscription.quoteData?.quoteDetails.riskData.vehicleDetails);

	const handleOnSubmit = (
		values: IChangeOfVehicleSubscriptionPart1,
		formikHelpers: FormikHelpers<IChangeOfVehicleSubscriptionPart1>,
	) => {
		if (isEmptyObject(vehicle.vehicleInfo)) {
			formikHelpers.setErrors({
				vrm: VEHICLE_DETAILS_PART_ONE_ERROR_MESSAGES.emptyVrm,
			});
			return;
		}
		if (vehicle.vehicleInfo.registrationNumber === previousVehicle?.registration?.replace(/\s/g, '').toUpperCase()) {
			formikHelpers.setErrors({
				vrm: VEHICLE_DETAILS_PART_ONE_ERROR_MESSAGES.sameVrm,
			});
			return;
		}
		if (vehicle.isCommercialVehicle) {
			formikHelpers.setErrors({
				vrm: VEHICLE_DETAILS_PART_ONE_ERROR_MESSAGES.vehicleTypeNotInsurable,
			});
			return;
		}
		const nextPagePath = ROUTE_PATHS.subscriptionRestartAmendVehicle2;
		navigate(nextPagePath, {
			state: {
				registration: values.vrm,
				yearOfManufacture: vehicle.vehicleInfo.year ?? 0,
				bodyType: values.bodyType ?? vehicle.vehicleInfo.bodyType?.code,
			},
		});
	};

	return (
		<JourneyCardLayout pageTitle='New Vehicle Details' loading={false}>
			<Typography
				sx={{
					margin: 'auto',
					paddingBottom: '1rem',
					textAlign: 'center',
				}}
			>
				Please enter a new vehicle registration below.
			</Typography>
			<Formik<IChangeOfVehicleSubscriptionPart1>
				initialValues={initialValues}
				validationSchema={amendVehiclePart1Schema}
				onSubmit={handleOnSubmit}
				validateOnBlur={true}
				validateOnChange={true}
			>
				{() => {
					return (
						<Form>
							<FormikLogErrors />
							<VehicleLookup />
							<ActionButtons>
								<BackButton
									onClick={() => {
										toggleModal(true);
									}}
								>
									Back
								</BackButton>
								<ContinueButton type='submit'>Continue</ContinueButton>
							</ActionButtons>
						</Form>
					);
				}}
			</Formik>
			<HelperModal
				open={open}
				onClose={() => toggleModal(false)}
				title='Discard The Changes?'
				description='Are you sure you wish to discard the changes?'
				closeText='No'
				submitText='Yes'
				onSubmit={() => {
					navigate(-1);
				}}
			/>
		</JourneyCardLayout>
	);
};

export default AmendVehiclePart1;
